/**
 * Usage & Limits (ticket 2026-10-18-usage-limits-and-traffic): partner-facing names of the
 * measured budgets and plans, and value formatting shared by the usage pages, the Partner
 * Administration limits card and the global "usage limit reached" notice.
 */

export const USAGE_AND_LIMITS_ROUTE_NAME = 'usageAndLimits';
export const USAGE_AND_LIMITS_PATH = '/ai-at-ryd/usage-and-limits';

export type UsageMeasure =
  | 'requests_min'
  | 'requests_day'
  | 'rows_day'
  | 'bytes_day'
  | 'exports_day'
  | 'reports_day'
  | 'ai_tokens_day'
  | 'writes_day'
  | 'downloads_day';

export type UsageLevel = 'company' | 'user' | 'client';

type UnitKind = 'count' | 'bytes' | 'tokens';

interface MeasureInfo {
  label: string;
  /** Short phrase for messages: "Usage limit reached: <phrase>." */
  phrase: string;
  unit: UnitKind;
  /** Noun after a count, e.g. "1,200 requests". */
  noun: string;
  window: 'minute' | 'day';
}

export const USAGE_MEASURES: Record<UsageMeasure, MeasureInfo> = {
  requests_min: {
    label: 'Requests Per Minute',
    phrase: 'requests per minute',
    unit: 'count',
    noun: 'requests',
    window: 'minute',
  },
  requests_day: {
    label: 'Requests Per Day',
    phrase: 'requests per day',
    unit: 'count',
    noun: 'requests',
    window: 'day',
  },
  rows_day: {
    label: 'Records Returned Per Day',
    phrase: 'records returned per day',
    unit: 'count',
    noun: 'records',
    window: 'day',
  },
  bytes_day: {
    label: 'Data Volume Per Day',
    phrase: 'data volume per day',
    unit: 'bytes',
    noun: '',
    window: 'day',
  },
  exports_day: {
    label: 'Exports Per Day',
    phrase: 'exports per day',
    unit: 'count',
    noun: 'exports',
    window: 'day',
  },
  reports_day: {
    label: 'Reports Per Day',
    phrase: 'reports per day',
    unit: 'count',
    noun: 'reports',
    window: 'day',
  },
  ai_tokens_day: {
    label: 'AI Tokens Per Day',
    phrase: 'AI tokens per day',
    unit: 'tokens',
    noun: 'tokens',
    window: 'day',
  },
  writes_day: {
    label: 'Changes Per Day',
    phrase: 'changes per day',
    unit: 'count',
    noun: 'changes',
    window: 'day',
  },
  downloads_day: {
    label: 'Downloads Per Day',
    phrase: 'downloads per day',
    unit: 'count',
    noun: 'downloads',
    window: 'day',
  },
};

export const USAGE_MEASURE_ORDER: UsageMeasure[] = [
  'requests_min',
  'requests_day',
  'rows_day',
  'bytes_day',
  'exports_day',
  'reports_day',
  'downloads_day',
  'writes_day',
  'ai_tokens_day',
];

export const USAGE_LEVEL_LABELS: Record<UsageLevel, string> = {
  company: 'Company',
  user: 'Per User',
  client: 'Per AI Agent Or API Client',
};

export const USAGE_PLAN_LABELS: Record<string, string> = {
  RYD_FLEET_FREE: 'ryd fleet free',
  RYD_FLEET_PLUS: 'ryd fleet plus',
  RYD_FLEET_ENTERPRISE: 'ryd fleet enterprise',
  API_PARTNER: 'API partner',
  INTERNAL: 'ryd internal',
};

export function measureLabel(measure: string): string {
  return USAGE_MEASURES[measure as UsageMeasure]?.label ?? measure;
}

export function measurePhrase(measure: string): string {
  return USAGE_MEASURES[measure as UsageMeasure]?.phrase ?? 'usage';
}

export function formatCount(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(Math.round(Number(value) || 0));
}

/** Bytes as B, KB, MB, GB, TB (base 1024, one decimal above KB). */
export function formatBytes(value: number | null | undefined): string {
  let n = Number(value) || 0;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unit = 0;
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? Math.round(n) : n.toFixed(1)} ${units[unit]}`;
}

/** A measure's value with its unit: "1.2 GB", "15,000 records", "800,000 tokens". */
export function formatMeasureValue(measure: string, value: number | null | undefined): string {
  const info = USAGE_MEASURES[measure as UsageMeasure];
  if (!info) return formatCount(value);
  if (info.unit === 'bytes') return formatBytes(value);
  return `${formatCount(value)}${info.noun ? ` ${info.noun}` : ''}`;
}

/** "45 s", "12 min", "3 h 20 min". */
export function formatDuration(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Whether the signed-in portal user is ryd staff (also when switched into a partner company). */
export function isInternalViewer(
  user: { userGroup?: string; partnerTypes?: string[]; email?: string } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.userGroup === 'ryd') return true;
  if ((user.partnerTypes || []).includes('RYD_INTERNAL')) return true;
  return /@ryd\.one$/i.test(String(user.email || '').trim());
}

/**
 * The documented limits per plan come from rp-users `GET /usage/plans` (the same values every
 * backend enforces with), so no backend rule ships in the portal bundle. Only the labels live
 * here.
 */

export type EnforcementMode = 'enforce' | 'observe';

export interface UsagePlanDto {
  key: string;
  level: UsageLevel;
  limits: Partial<Record<UsageMeasure, number>>;
}

export interface UsagePlansDto {
  plans: UsagePlanDto[];
  enforcement: Record<UsageLevel, Partial<Record<UsageMeasure, EnforcementMode>>>;
}

export interface PlanLimitColumn extends UsagePlanDto {
  label: string;
}

const LEVEL_PLAN_LABELS: Record<string, string> = {
  USER: USAGE_LEVEL_LABELS.user,
  CLIENT: USAGE_LEVEL_LABELS.client,
};

export function buildPlanColumns(plans: UsagePlanDto[]): PlanLimitColumn[] {
  return plans.map((plan) => ({
    ...plan,
    label: USAGE_PLAN_LABELS[plan.key] ?? LEVEL_PLAN_LABELS[plan.key] ?? plan.key,
  }));
}

/** Whether a measure will be enforced at a level once enforcement starts (else measured only). */
export function isEnforced(
  enforcement: UsagePlansDto['enforcement'] | null | undefined,
  level: UsageLevel,
  measure: UsageMeasure,
): boolean {
  return enforcement?.[level]?.[measure] === 'enforce';
}
